/**
 * Client-side runner: dispatches image processing to a Web Worker when the
 * platform supports OffscreenCanvas, otherwise runs on the main thread.
 */

import { hasOffscreen } from "./canvas";
import {
  processCardImage,
  type StudioOptions,
  type StudioResult,
  type StudioStage,
} from "./studio";

let worker: Worker | null = null;
let workerBroken = false;
/** Listeners of in-flight jobs, so a worker crash fails them immediately. */
const inflight = new Set<(reason: Error) => void>();

function breakWorker(reason: string) {
  workerBroken = true;
  try {
    worker?.terminate();
  } catch {
    /* already gone */
  }
  worker = null;
  for (const fail of [...inflight]) fail(new Error(reason));
  inflight.clear();
}

function getWorker(): Worker | null {
  if (workerBroken || typeof window === "undefined" || !hasOffscreen()) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL("./studio.worker.ts", import.meta.url), {
      type: "module",
    });
    worker.onerror = () => breakWorker("Image worker crashed");
    return worker;
  } catch {
    workerBroken = true;
    return null;
  }
}


export function runStudio(
  file: File,
  options: StudioOptions = {},
  onStage?: (stage: StudioStage) => void,
): Promise<StudioResult> {
  const w = getWorker();
  if (!w) return processCardImage(file, options, onStage);

  const id = crypto.randomUUID();
  return new Promise<StudioResult>((resolve, reject) => {
    let settled = false;

    const fallback = () => {
      if (settled) return;
      settled = true;
      cleanup();
      processCardImage(file, options, onStage).then(resolve, reject);
    };

    const timeout = setTimeout(() => {
      // Worker stalled (older Safari OffscreenCanvas quirks) — fall back.
      breakWorker("Image worker timed out");
    }, 90_000);

    function cleanup() {
      clearTimeout(timeout);
      inflight.delete(fallback);
      w!.removeEventListener("message", onMessage);
    }

    function onMessage(e: MessageEvent) {
      const d = e.data;
      if (!d || d.id !== id) return;
      if (d.type === "stage") {
        onStage?.(d.stage as StudioStage);
        return;
      }
      if (settled) return;
      settled = true;
      cleanup();
      if (d.type === "done") resolve(d.result as StudioResult);
      else reject(new Error(d.message ?? "Image processing failed"));
    }

    // A worker crash or timeout retries the job on the main thread rather
    // than leaving the upload stuck in "Processing" forever.
    inflight.add(fallback);
    w.addEventListener("message", onMessage);
    try {
      w.postMessage({ id, file, options });
    } catch {
      breakWorker("Image worker rejected the job");
    }
  });
}

/** Free the worker (and its GPU/canvas memory) when the uploader unmounts. */
export function terminateStudioWorker() {
  try {
    worker?.terminate();
  } catch {
    /* already gone */
  }
  worker = null;
}

